/**
 * Checks if the application is running in standalone mode (installed as a PWA)
 * @returns {boolean} True if the app is currently running in PWA mode
 */
export function isPwa() {
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
  return isStandalone;
}

/**
 * Checks if the PWA application is installed using getInstalledRelatedApps API
 * Only available on Android
 * @returns {Promise<boolean>} Resolves to true if the PWA is installed
 */
export async function isPwaInstalled(log) {
  // Only available on Android
  if (!isAndroid() || !("getInstalledRelatedApps" in navigator)) {
    log("getInstalledRelatedApps not supported");
    return false;
  }

  try {
    const relatedApps = await navigator.getInstalledRelatedApps();
    log("Related apps found:", relatedApps.length > 0, relatedApps);
    return relatedApps.length > 0;
  } catch (error) {
    log("Error checking for installed related apps:", error);
    return false;
  }
}

/**
 * Checks if the device is running Android
 * @returns {boolean} True if Android device
 */
export function isAndroid() {
  return /android/.test(navigator.userAgent.toLowerCase());
}

/**
 * Checks if the device is running iOS
 * @returns {boolean} True if iOS device
 */
export function isiOS() {
  return /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
}

/**
 * Checks if the browser is Firefox
 * @returns {boolean} True if Firefox browser
 */
export function isFirefox() {
  return /firefox/.test(navigator.userAgent.toLowerCase());
}

/**
 * Checks if the device is running Android with Google Chrome browser
 * @returns {boolean} True if device is Android and using Google Chrome
 */
export function isAndroidGoogleChrome() {
  if (!isAndroid()) {
    return false;
  }

  if (navigator.userAgentData) {
    // 1) User-Agent Client Hints (modern Chrome)
    return navigator.userAgentData.brands.some((b) => b.brand === "Google Chrome");
  } else {
    // 2) Fall back to UA‑string sniffing
    const ua = navigator.userAgent;
    // Must have the Chrome token...
    const isChrome = /\bChrome\/\d+/.test(ua);
    // ...but not Edge (Edg/ or EdgA/), Firefox, or SamsungBrowser
    const edge = /EdgA?\//.test(ua);
    const firefox = /Firefox\/\d+/.test(ua);
    const samsung = /SamsungBrowser\/\d+/.test(ua);
    const opera = /OPR\/\d+/.test(ua);

    return isChrome && !edge && !firefox && !samsung && !opera;
  }
}

/**
 * Checks if push notifications are supported in current browser
 * @returns {boolean} True if push is supported
 */
export function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window;
}

/**
 * Gets the PushManager instance from the service worker registration
 * @returns {Promise<PushManager|null>} The PushManager instance or null if not supported/registered
 */
export async function getPushManager() {
  return (await getRegistration())?.pushManager;
}

/**
 * Gets the service worker registration when it's ready
 * @returns {Promise<ServiceWorkerRegistration|null>} The ready service worker registration or null if not supported
 */
export async function getRegistration() {
  if (isPushSupported()) {
    return await navigator.serviceWorker.ready;
  } else {
    return null;
  }
}

/**
 * Gets the current push notification subscription
 * @returns {Promise<PushSubscription|null>} The current push subscription or null if not available
 */
export async function getSubscription() {
  const pushManager = await getPushManager();
  if (pushManager) {
    return await pushManager.getSubscription();
  } else {
    return null;
  }
}

/**
 * Gets the endpoint URL from the current push subscription
 * @returns {Promise<string|null>} The subscription endpoint URL or null if not subscribed
 */
export async function getPushSubscriptionEndpoint() {
  return (await getSubscription())?.endpoint;
}

/**
 * Logs device and browser information for debugging
 * @param {Function} log - The logger function to use
 */
export function logDeviceInfo(log) {
  try {
    log("userAgent:", navigator?.userAgent);
    log("userAgentData:", navigator?.userAgentData?.toJSON());
    log("platform:", navigator?.platform);
    log("isAndroid:", isAndroid());
    log("isiOS:", isiOS());
    log("isFirefox:", isFirefox());
    log("isPwa:", isPwa());
    log("has touch:", "ontouchstart" in window);
    log("coarse pointer:", window.matchMedia("(pointer: coarse)").matches);
  } catch (e) {
    log("logDeviceInfo error:", e.message);
  }
}
